import { useNavigate as useRouterNavigate } from 'react-router-dom'
import { Redirect } from '@shopify/app-bridge/actions'
import { getAppBridgeInstance, getHost } from '@utils/appBridge'
/**
 * Kiểm tra url có phải absolute (chứa domain) hay không
 * - Bắt đầu bằng http://, https://, //
 * - Hoặc là domain (có ít nhất 1 dấu chấm, không bắt đầu bằng /)
 * - Ngược lại là path
 */
function isAbsoluteUrl(url: string): boolean {
  if (/^(https?:)?\/\//.test(url)) return true
  // Không bắt đầu bằng / và có ít nhất 1 dấu chấm (.) => domain
  return !url.startsWith('/') && /\./.test(url)
}
/**
 * Chuẩn hoá url: xoá http:// hoặc https:// nếu có, sau đó thêm https:// vào đầu url
 * @param url
 */
function normalizeToHttps(url: string): string {
  const noSchema = url.replace(/^(https?:)?\/\//, '')
  return `https://${noSchema}`
}
/**
 * Hook chọn navigate phù hợp giữa Shopify App Bridge và React Router
 * @returns navigate function (chấp nhận string hoặc object { url, target })
 */
export function useCustomNavigate(): (
  to: string | { url: string; target?: 'host' | 'new' | 'self' }
) => void {
  const routerNavigate = useRouterNavigate()
  const host = getHost()
  const app = host ? getAppBridgeInstance(host) : null
  const redirect = app ? Redirect.create(app) : null

  return (to: string | { url: string; target?: 'host' | 'new' | 'self'; newContext?: boolean }) => {
    if (typeof to === 'string') {
      if (typeof __E2E__ !== 'undefined' && __E2E__) {
        routerNavigate(to)
      } else {
        if (isAbsoluteUrl(to)) {
          redirect?.dispatch(Redirect.Action.REMOTE, {
            url: normalizeToHttps(to),
            newContext: true,
          })
        } else {
          // Use React Router for internal navigation to prevent app remounting
          routerNavigate(to)
        }
      }
    } else if (typeof to === 'object' && to !== null && typeof to.url === 'string') {
      const { url, target } = to
      // Nếu là absolute URL, giữ nguyên scheme
      if (typeof __E2E__ !== 'undefined' && __E2E__) {
        routerNavigate(url)
      } else {
        if (isAbsoluteUrl(url)) {
          const new_context = to.newContext || target === 'host' || target === 'new'
          if (redirect) {
            redirect.dispatch(Redirect.Action.REMOTE, {
              url: normalizeToHttps(url),
              newContext: new_context,
            })
          } else {
            window.open(
              normalizeToHttps(url),
              new_context ? '_blank' : '_top',
            )
          }
        } else {
          // Use React Router for internal navigation to prevent app remounting
          routerNavigate(url)
        }
      }
    } else {
      throw new Error(
        'Invalid argument passed to useCustomNavigate. Must be string or { url } object.'
      )
    }
  }
}

